/*
 * ELASTICSEARCH CONFIDENTIAL
 * __________________
 *
 *  Copyright Elasticsearch B.V. All rights reserved.
 *
 * NOTICE:  All information contained herein is, and remains
 * the property of Elasticsearch B.V. and its suppliers, if any.
 * The intellectual and technical concepts contained herein
 * are proprietary to Elasticsearch B.V. and its suppliers and
 * may be covered by U.S. and Foreign Patents, patents in
 * process, and are protected by trade secret or copyright
 * law.  Dissemination of this information or reproduction of
 * this material is strictly forbidden unless prior written
 * permission is obtained from Elasticsearch B.V.
 */

import React, { Fragment, useState } from 'react'
import { FormattedMessage, defineMessages, injectIntl } from 'react-intl'

import type { EuiContextMenuPanelDescriptor } from '@elastic/eui'
import { EuiContextMenu, EuiIcon, EuiPopover } from '@elastic/eui'

import type { CoordinatorCandidateInfo } from '@modules/cloud-api/v1/types'
import { CuiButton } from '@modules/cui/Button'
import { useToggle } from '@modules/utils/hooks/useToggle'

import DeleteHost from '../DeleteHost'
import DemoteHost from '../DemoteHost'

import type { IntlShape } from 'react-intl'

type Props = {
  intl: IntlShape
  regionId: string
  hostId: string
  coordinator?: CoordinatorCandidateInfo
  startManagingRoles: () => void
}

const messages = defineMessages({
  manageRoles: { id: 'host-overview.manage-roles', defaultMessage: 'Manage roles' },
  demoteHost: { id: 'host-overview.demote-host', defaultMessage: 'Demote host' },
  deleteHost: { id: 'host-overview.delete-host', defaultMessage: 'Delete host' },
})

const HostMenu: React.FC<Props> = ({
  intl: { formatMessage },
  regionId,
  hostId,
  coordinator,
  startManagingRoles,
}) => {
  const [demotingHost, setDemotingHost] = useState(false)

  const [deletingHost, setDeletingHost] = useState(false)

  const [isPopoverOpen, togglePopover] = useToggle(false)

  const manageHostPanels: EuiContextMenuPanelDescriptor[] = [
    {
      id: 0,
      items: [
        {
          name: formatMessage(messages.manageRoles),
          icon: <EuiIcon type='gear' size='m' />,
          onClick: () => {
            togglePopover()
            startManagingRoles()
          },
        },

        ...(coordinator
          ? [
              {
                name: formatMessage(messages.demoteHost),
                icon: <EuiIcon type='sortDown' size='m' />,
                onClick: () => {
                  togglePopover()
                  setDemotingHost(true)
                },
              },
            ]
          : [
              {
                name: formatMessage(messages.deleteHost),
                icon: <EuiIcon type='trash' size='m' />,
                onClick: () => {
                  togglePopover()
                  setDeletingHost(true)
                },
              },
            ]),
      ],
    },
  ]

  return (
    <Fragment>
      <EuiPopover
        id='popover'
        button={
          <CuiButton iconType='arrowDown' iconSide='right' onClick={togglePopover}>
            <FormattedMessage id='host.manage-host' defaultMessage='Manage host' />
          </CuiButton>
        }
        isOpen={isPopoverOpen}
        closePopover={togglePopover}
        anchorPosition='downRight'
        panelPaddingSize='none'
      >
        <EuiContextMenu initialPanelId={0} panels={manageHostPanels} />
      </EuiPopover>

      {demotingHost && (
        <DemoteHost regionId={regionId} hostId={hostId} onClose={() => setDemotingHost(false)} />
      )}

      {deletingHost && (
        <DeleteHost regionId={regionId} hostId={hostId} onClose={() => setDeletingHost(false)} />
      )}
    </Fragment>
  )
}

export default injectIntl(HostMenu)
